import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  TextField,
} from '@mui/material';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuthContext } from '../../Context/AuthContext/AuthContext';
import { useAddressChangeQuery } from '../../rest/useAddressChange';
import CommonOfflinePaymentInfo from '../new-member-applications/IndexFile/paymentInformation/offline/CommonOfflinePaymentInfo';
import AddressSelect from './AddressSelect';
import axios from 'axios';
import { url } from '../../Helpers/utils/Constant';
import { getToken } from '../../rest/utils/get-token';
import { showToast } from '../../Toaster/Toaster';
import ImageUploaderWithPreview from '../../Common/ImageUploaderWithPreview';

type IProps = {
  handelClose: Dispatch<SetStateAction<boolean>>;
  setReload: Dispatch<SetStateAction<boolean>>;
};

const CreateAddressChangeApplication = ({ handelClose, setReload }: IProps) => {
  const { user } = useAuthContext();
  const { data: addressDraftList, fetchData: fetchAddressDraft } =
    useAddressChangeQuery();
  const draftData = addressDraftList?.[0];

  useEffect(() => {
    if (user?.user_member_id) {
      fetchAddressDraft({
        address_change_application_member_id: user.user_member_id,
        address_change_application_status: 'draft',
      });
    }
  }, [user?.user_member_id, fetchAddressDraft]);

  const {
    handleSubmit,
    register,
    clearErrors,
    setError,
    formState: { errors },
    setValue,
    control,
    watch,
    getValues,
  } = useForm<any>({
    defaultValues: {
      bank_ac_no: '00890320000141',
      amount: '',
      bank_name: 'Trust Bank (Kakrail Branch)',
    },
  });

  const amount = watch('amount');
  const [areaID, setAreaID] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  const token = getToken();

  useEffect(() => {
    axios
      .get(`${url}/api/atab/payment-amount?application_type=ADDRESS_CHANGE`)
      .then((res) => {
        if (res.data.success) {
          setValue('amount', res.data.data.amount);
        }
      });
  }, [setValue]);

  useEffect(() => {
    if (draftData) {
      if (draftData.address_change_application_email) {
        setValue(
          'address_change_application_email',
          draftData.address_change_application_email,
        );
      }
      if (draftData.address_change_application_phone) {
        setValue(
          'address_change_application_phone',
          draftData.address_change_application_phone,
        );
      }
      if (draftData.address_change_application_cellphone) {
        setValue(
          'address_change_application_cellphone',
          draftData.address_change_application_cellphone,
        );
      }
      if (draftData.address_change_application_addressline) {
        setValue(
          'address_change_application_addressline',
          draftData.address_change_application_addressline,
        );
      }
      if (draftData.address_change_application_postal_code) {
        setValue(
          'address_change_application_postal_code',
          draftData.address_change_application_postal_code,
        );
      }
      if (draftData.address_change_application_police_station) {
        setValue(
          'address_change_application_police_station',
          draftData.address_change_application_police_station,
        );
      }
      if (draftData.area_id) {
        setAreaID(String(draftData.area_id));
      }
    }
  }, [draftData, setValue]);

  const onSubmit = async (data: any, asDraft: boolean = false) => {
    const formData = new FormData();

    const paymentFields = [
      'type',
      'bank_ac_no',
      'amount',
      'bank_name',
      'deposit_date',
      'branch_name',
      'reference_no',
      'bank_slip',
    ];

    if (asDraft) {
      formData.append('status', 'draft');
      formData.append('address_change_application_status', 'draft');
    } else {
      if (data?.type === 'online') {
        formData.append(
          'success_page',
          `${window.location.origin}/payment-success`,
        );
        formData.append(
          'failed_page',
          `${window.location.origin}/payment-failed`,
        );
        formData.append(
          'cancelled_page',
          `${window.location.origin}/payment-reject`,
        );
      }
    }

    Object.keys(data).forEach((item) => {
      if (asDraft && paymentFields.includes(item)) {
        return;
      }
      if (!asDraft && data?.type !== 'offline' && paymentFields.includes(item) && item !== 'type') {
        return;
      }

      if (typeof data[item] === 'object' && data[item] !== null) {
        if (data[item].length > 0) {
          formData.append(item, data[item][0]);
        }
      } else if (
        data[item] !== undefined &&
        data[item] !== null &&
        data[item] !== ''
      ) {
        formData.append(item, data[item]);
      }
    });

    formData.append(
      'address_change_application_member_id',
      user.user_member_id?.toString(),
    );
    if (areaID) {
      formData.append('address_change_application_area_id', areaID);
    }

    try {
      setLoading(true);
      setIsDraft(asDraft);
      const response = await fetch(`${url}/api/member/address-change-application`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token || ''}`,
        },
        body: formData,
      });

      const responseData = await response.json();

      if (responseData.success) {
        if (!asDraft && responseData?.data?.redirect_url) {
          window.open(
            responseData?.data?.redirect_url,
            '_blank',
            'noopener,noreferrer',
          );
        }
        showToast(
          responseData.message ||
          (asDraft
            ? 'Application saved as draft successfully'
            : 'Application submitted successfully'),
          'success',
        );
        handelClose(false);
        setReload((prev) => !prev);
      } else {
        setLoading(false);
        setIsDraft(false);
        showToast(
          responseData.message ||
          (asDraft
            ? 'Failed to save application as draft'
            : 'Failed to submit application'),
          'error',
        );
      }
    } catch (error: any) {
      showToast(error?.message || 'An error occurred', 'error');
      setLoading(false);
      setIsDraft(false);
    }
  };

  const handleSaveDraft = () => {
    const values = getValues();
    onSubmit(values, true);
  };

  return (
    <Box
      component='form'
      onSubmit={handleSubmit((data) => onSubmit(data, false))}
      p={2}
    >
      <Grid container spacing={2} mb={2}>
        <Grid xs={12} sm={6} md={4} item>
          <TextField
            fullWidth
            label='Email'
            required
            id='outlined-email'
            type='email'
            InputLabelProps={{ shrink: true }}
            {...register('address_change_application_email')}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4} item>
          <TextField
            fullWidth
            label='Phone number'
            id='outlined-phone'
            type='number'
            InputLabelProps={{ shrink: true }}
            {...register('address_change_application_phone')}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4} item>
          <TextField
            fullWidth
            label='Cellphone'
            id='outlined-cellphone'
            type='number'
            InputLabelProps={{ shrink: true }}
            {...register('address_change_application_cellphone')}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4} item>
          <TextField
            fullWidth
            label='Address'
            id='outlined-address'
            type='text'
            InputLabelProps={{ shrink: true }}
            {...register('address_change_application_addressline')}
          />
        </Grid>

        <Grid xs={12} sm={6} md={4} item>
          <TextField
            fullWidth
            label='Postal code'
            required
            id='outlined-postal'
            type='number'
            InputLabelProps={{ shrink: true }}
            {...register('address_change_application_postal_code')}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4} item>
          <TextField
            fullWidth
            label='Police station'
            required
            id='outlined-police'
            type='text'
            InputLabelProps={{ shrink: true }}
            {...register('address_change_application_police_station')}
          />
        </Grid>

        <AddressSelect
          setAreaID={setAreaID}
          companyData={draftData}
        />

        <Grid xs={12} sm={6} md={4} item>
          <ImageUploaderWithPreview
            register={register}
            setError={setError}
            clearErrors={clearErrors}
            errors={errors}
            label='Civil aviation file ( max size 5mb )'
            fileName='address_change_application_civil_aviation'
            required
            imgSerUrl={
              draftData?.address_change_application_civil_aviation || null
            }
          />
        </Grid>

        <Grid xs={12} sm={6} md={4} item>
          <ImageUploaderWithPreview
            register={register}
            setError={setError}
            clearErrors={clearErrors}
            errors={errors}
            label='Trade license file ( max size 5mb )'
            fileName='address_change_application_trade_license'
            required
            imgSerUrl={
              draftData?.address_change_application_trade_license || null
            }
          />
        </Grid>

        <Grid xs={12} sm={6} md={4} item>
          <ImageUploaderWithPreview
            register={register}
            setError={setError}
            clearErrors={clearErrors}
            errors={errors}
            label='Forwarding letter ( max size 5mb )'
            fileName='address_change_application_form'
            required
            imgSerUrl={
              draftData?.address_change_application_form || null
            }
          />
        </Grid>

        <Grid xs={12} item sx={{ my: 2 }}>
          <Divider>
            <Chip label='Payment Information' size='small' />
          </Divider>
        </Grid>
        <Grid xs={12} item>
          <CommonOfflinePaymentInfo
            clearErrors={clearErrors}
            setError={setError}
            errors={errors}
            register={register}
            setValue={setValue}
            control={control}
            onlinePaymentAmount={amount}
          />
        </Grid>
      </Grid>

      <Stack
        direction={{ xs: 'column-reverse', sm: 'row' }}
        spacing={2}
        justifyContent='flex-end'
        alignItems='center'
        mt={4}
      >
        <Button
          type='button'
          variant='outlined'
          color='primary'
          disabled={loading}
          onClick={handleSaveDraft}
          startIcon={
            loading && isDraft ? (
              <CircularProgress size={18} color='inherit' />
            ) : null
          }
          sx={{
            width: { xs: '100%', sm: 'auto' },
            minWidth: 160,
            py: 1,
            fontWeight: 600,
            fontSize: '0.95rem',
          }}
        >
          <span>{loading && isDraft ? 'Saving Draft...' : 'Save As Draft'}</span>
        </Button>

        <Button
          type='submit'
          variant='contained'
          color='primary'
          disabled={loading}
          disableElevation
          startIcon={
            loading && !isDraft ? (
              <CircularProgress size={18} color='inherit' />
            ) : null
          }
          sx={{
            width: { xs: '100%', sm: 'auto' },
            minWidth: 160,
            py: 1,
            fontWeight: 600,
            fontSize: '0.95rem',
          }}
        >
          <span>{loading && !isDraft ? 'Submitting...' : 'Submit'}</span>
        </Button>
      </Stack>
    </Box>
  );
};

export default CreateAddressChangeApplication;

